'use strict';

import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import classNames from 'classnames';

/**
 * DrawerMenu - Functional component version for Plans/Editor
 *
 * Creates a circular menu that slides out from the side when clicked.
 * Simplified version without legacy patterns.
 */
const DrawerMenu = ({
    button,
    children,
    menuTitle,
    className,
    modalClassName,
    onOpenMenu,
    checkForOnlyOneSwapOption
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [drawer, setDrawer] = useState(false);

    const closeModal = useCallback((immediate = false) => {
        if (immediate) {
            setIsModalOpen(false);
            setDrawer(false);
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            setDrawer(false);
            setTimeout(() => {
                setIsModalOpen(false);
                resolve();
            }, 250);
        });
    }, []);

    const toggleDrawer = useCallback((e) => {
        e.stopPropagation();

        if (isModalOpen) {
            return closeModal();
        }

        // Check if there's only one swap option (auto-select)
        if (checkForOnlyOneSwapOption && checkForOnlyOneSwapOption()) {
            return;
        }

        // Call onOpenMenu if provided
        onOpenMenu && onOpenMenu();

        // Open modal first, then animate drawer
        setIsModalOpen(true);
        setDrawer(false);

        // Use effect to trigger drawer animation after modal opens
        setTimeout(() => setDrawer(true), 0);
    }, [isModalOpen, closeModal, checkForOnlyOneSwapOption, onOpenMenu]);

    const handleOverlayClick = useCallback((e) => {
        e.stopPropagation();

        // Only close if clicking on the overlay, not the content
        if (e.target === e.currentTarget) {
            closeModal();
        }
    }, [closeModal]);

    return (
        <>
            <button
                onClick={toggleDrawer}
                className={classNames("drawer-menu-btn", className)}
            >
                {button}
            </button>

            <Modal
                isOpen={isModalOpen}
                onRequestClose={handleOverlayClick}
                className={classNames(
                    "drawer-menu-modal",
                    modalClassName,
                    drawer ? 'drawer-modal-after-open' : null
                )}
                overlayClassName={classNames(
                    "drawer-menu-modal-overlay",
                    drawer ? 'drawer-overlay-after-open' : null
                )}
                contentLabel="Drawer menu"
                closeTimeoutMS={500}
            >
                <div
                    className="drawer-menu-modal-container"
                    onClick={handleOverlayClick}
                >
                    <header>
                        {menuTitle && <h3>{menuTitle}</h3>}
                        <button
                            className="menu-item cancel-btn"
                            onClick={() => closeModal()}
                        >
                            Cancel <i className="icon-close-x" />
                        </button>
                    </header>
                    <div className="inner-container">
                        <div className="child-container">
                            {typeof children === 'function'
                                ? children({ closeModal })
                                : children
                            }
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

DrawerMenu.propTypes = {
    button: PropTypes.node,
    children: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.func
    ]),
    menuTitle: PropTypes.string,
    className: PropTypes.string,
    modalClassName: PropTypes.string,
    onOpenMenu: PropTypes.func,
    checkForOnlyOneSwapOption: PropTypes.func,
};

export default DrawerMenu;
